import type { ExportFailureCode, exportFailureDiagnostics } from '../shared/export-failure.js';
import { z } from 'zod';
import { createCheckoutAttemptId } from './analytics-context.js';
import type { MapAnalyticsTrack } from '../shared/analytics-schema.js';

const taskSchema = z.object({ id: z.string().regex(/^[a-f0-9]{64}$/), startedAt: z.number() });
export type ExportTask = z.infer<typeof taskSchema>;
const flows = new Map<string, ExportTask>();
const key = (id: string) => `drawonamap.export-task.${id}`;
export function startExportTask(): ExportTask {
  return { id: createCheckoutAttemptId() ?? '', startedAt: Date.now() };
}
export function bindExportTask(flowId: string, task: ExportTask): void {
  flows.set(flowId, task);
  try { localStorage.setItem(key(flowId), JSON.stringify(task)); } catch {}
}
export function exportTaskForFlow(flowId: string): ExportTask | undefined {
  if (flows.has(flowId)) return flows.get(flowId);
  try {
    const parsed = taskSchema.safeParse(JSON.parse(localStorage.getItem(key(flowId)) ?? 'null'));
    if (parsed.success && Date.now() - parsed.data.startedAt < 35 * 86400000) return parsed.data;
  } catch {}
  return undefined;
}
export function trackExportDelivery(track: MapAnalyticsTrack, flowId: string, format: 'pdf' | 'png',
  outcome: 'started' | 'failed' | 'confirmed', recovery = false): void {
  const task = exportTaskForFlow(flowId);
  track('export_delivery', {
    export_flow_id: flowId, export_task_id: task?.id, format, outcome,
    recovery: String(recovery),
    elapsed_ms: task ? String(Math.min(9999999999, Math.max(0, Date.now() - task.startedAt))) : undefined,
  });
}

export function trackExportFailure(track: MapAnalyticsTrack, flowId: string, format: 'pdf' | 'png', stage: 'checkout' | 'capture', failureCode: ExportFailureCode = 'unknown', diagnostics: ReturnType<typeof exportFailureDiagnostics> = {}): void {
  track('export_task_failed', { ...diagnostics, export_flow_id: flowId, export_task_id: exportTaskForFlow(flowId)?.id, format, stage, failure_code: failureCode });
}
